import {
  createContext,
  HTMLProps,
  PropsWithChildren,
  ReactNode,
  useContext,
  useId,
  useMemo,
} from "react";
import * as tooltip from "@zag-js/tooltip";
import { normalizeProps, useMachine } from "@zag-js/react";
import { Portal } from "@headlessui/react";
import { classNames } from "../../utils/class-names";

const TooltipContext = createContext<{
  api: ReturnType<(typeof tooltip)["connect"]>;
  disableCloseOnClick: boolean;
  off: boolean;
}>(null!);

type Placement = NonNullable<
  NonNullable<tooltip.Context["positioning"]>["placement"]
>;

interface TooltipProps {
  placement?: Placement;
  openDelay?: number;
  closeDelay?: number;
  disableCloseOnClick?: boolean;
  off?: boolean;
  gutter?: number;
  children?: ReactNode;
}

export const Tooltip = ({
  openDelay = 200,
  closeDelay = 500,
  placement = "right",
  disableCloseOnClick = false,
  off = false,
  children,
  gutter,
}: TooltipProps): JSX.Element => {
  const id = useId();

  const positioning = useMemo(() => {
    const position = { placement, gutter };
    if (gutter === undefined) {
      // zagjs / floating-ui treats an `undefined` gutter
      // and a _not set_ gutter option differently.
      // and we want the latter
      delete position.gutter;
    }

    return position;
  }, [placement, gutter]);
  const [state, send] = useMachine(
    tooltip.machine({
      id,
      openDelay,
      closeDelay,
      closeOnPointerDown: false,
      positioning,
    })
  );
  const api = tooltip.connect(state, send, normalizeProps);

  return (
    <TooltipContext.Provider value={{ api, disableCloseOnClick, off }}>
      {children}
    </TooltipContext.Provider>
  );
};

export const TooltipOrigin = (props: HTMLProps<HTMLDivElement>) => {
  const { api, disableCloseOnClick, off } = useContext(TooltipContext);

  const triggerProps = off ? {} : api.triggerProps;
  const overrides = disableCloseOnClick ? { onClick: undefined } : {};

  return (
    <div
      {...props}
      {...triggerProps}
      {...overrides}
      className={props.className ?? "inline-block"}
    />
  );
};

interface TooltipContentProps {
  className?: string;
}

export const TooltipContent = ({
  className,
  children,
}: PropsWithChildren<TooltipContentProps>) => {
  const { api } = useContext(TooltipContext);

  if (!api.isOpen) return null;

  return (
    <Portal>
      <div {...api.positionerProps} className="z-20">
        <div {...api.arrowProps}>
          <div {...api.arrowProps} />
        </div>
        <div
          {...api.contentProps}
          className={classNames(
            `bg-shale-750 inline-block p-5 text-shale-100 rounded-lg text-base font-sans max-w-sm shadow-md`,
            className
          )}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
};
